import { LinkIcon, LocationIcon } from '@/assets/icons'
import { backgroundOrange } from '@/theme/colors'
import { AttachmentSection } from '@components/Attachments'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { EventCategory, PutEventType } from '@orbit/api-types'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { Event } from '@types'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { TimePickerWrapper } from '../AddEventModal/FormComponents.styles'
import {
	AddLinkBtn,
	ButtonRow,
	Container,
	EditButton,
	EmptyValue,
	FieldHeader,
	FieldLabel,
	FieldRow,
	FieldValue,
	LinkInfo,
	LinkInputWrapper,
	LinkListReadOnly,
	LinkPreview,
	LinkSource,
	LinkTextInput,
	LinkThumbnail,
	LinkUrl,
	LocationDisplay,
	LocationSecondary,
	PrimaryButton,
	RemoveLinkBtn,
	SecondaryButton,
	Select,
	StaticLinkPreview,
	Textarea,
	TitleInput,
} from './EventDetailsModal.styles'

const DescriptionGlyph: React.FC = () => (
	<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
		<line x1="4" y1="7" x2="20" y2="7" />
		<line x1="4" y1="12" x2="20" y2="12" />
		<line x1="4" y1="17" x2="14" y2="17" />
	</svg>
)

const getSourceColor = (source: string) => {
	switch (source.toLowerCase()) {
		case 'tiktok':
			return '#000000'
		case 'instagram':
			return '#E4405F'
		case 'youtube':
			return '#FF0000'
		case 'googlemaps':
			return '#4285F4'
		case 'applemaps':
			return '#1d1d1f'
		default:
			return '#6b7280'
	}
}

const formatSourceName = (source: string) => {
	if (source === 'GoogleMaps') return 'Google Maps'
	if (source === 'AppleMaps') return 'Apple Maps'
	return source
}

const EVENT_CATEGORIES: EventCategory[] = [
	'FoodAndDrink',
	'SeeAndDo',
	'Nature',
	'Shopping',
	'Accommodation',
	'Transportation',
	'Event',
	'Other',
]

const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Events',
	Other: 'Other',
}

interface EventDetailsModalProps {
	isOpen: boolean
	onClose: () => void
	onUpdate: (updates: PutEventType) => void
	onDelete?: () => void
	event: Event
}

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
	isOpen,
	onClose,
	onUpdate,
	onDelete,
	event,
}) => {
	const { tripId } = useParams<{ tripId: string }>()

	const [title, setTitle] = useState(event.name)
	useEffect(() => {
		if (isOpen) setTitle(event.name)
	}, [isOpen, event.name])

	const handleTitleBlur = () => {
		const trimmed = title.trim()
		if (!trimmed) {
			setTitle(event.name)
			return
		}
		if (trimmed !== event.name) {
			onUpdate({ name: trimmed })
		}
	}

	const [isEditingDescription, setIsEditingDescription] = useState(false)
	const [descriptionDraft, setDescriptionDraft] = useState(event.description || '')
	useEffect(() => {
		if (!isEditingDescription) setDescriptionDraft(event.description || '')
	}, [event.description, isEditingDescription])

	const handleSaveDescription = () => {
		onUpdate({ description: descriptionDraft.trim() || null })
		setIsEditingDescription(false)
	}

	const [isEditingLocation, setIsEditingLocation] = useState(false)
	const [locationText, setLocationText] = useState(event.location?.primaryText || '')
	const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(
		event.location?.googlePlaceId
			? {
					placeId: event.location.googlePlaceId,
					primaryText: event.location.primaryText || '',
					secondaryText: event.location.secondaryText || '',
					fullText: event.location.fullText,
					types: event.location.googleTypes || [],
				}
			: null,
	)
	useEffect(() => {
		if (!isEditingLocation) {
			setLocationText(event.location?.primaryText || '')
			setSelectedPlace(
				event.location?.googlePlaceId
					? {
							placeId: event.location.googlePlaceId,
							primaryText: event.location.primaryText || '',
							secondaryText: event.location.secondaryText || '',
							fullText: event.location.fullText,
							types: event.location.googleTypes || [],
						}
					: null,
			)
		}
	}, [event.location, isEditingLocation])

	const handleSaveLocation = () => {
		const location = selectedPlace
			? {
					fullText: selectedPlace.fullText,
					primaryText: selectedPlace.primaryText,
					secondaryText: selectedPlace.secondaryText || undefined,
					googlePlaceId: selectedPlace.placeId,
					googleTypes: selectedPlace.types,
				}
			: locationText.trim()
				? {
						fullText: locationText.trim(),
						primaryText: locationText.trim(),
						googleTypes: [],
					}
				: null
		onUpdate({ location })
		setIsEditingLocation(false)
	}

	const handleClearLocation = () => {
		onUpdate({ location: null })
		setSelectedPlace(null)
		setLocationText('')
		setIsEditingLocation(false)
	}

	const [isEditingLinks, setIsEditingLinks] = useState(false)
	const [linksDraft, setLinksDraft] = useState<string[]>(
		event.tripEventLinks?.map(l => l.url) || [],
	)
	useEffect(() => {
		if (!isEditingLinks) {
			setLinksDraft(event.tripEventLinks?.map(l => l.url) || [])
		}
	}, [event.tripEventLinks, isEditingLinks])

	useEffect(() => {
		if (!isOpen) {
			setIsEditingDescription(false)
			setIsEditingLocation(false)
			setIsEditingLinks(false)
		}
	}, [isOpen])

	const handleSaveLinks = () => {
		const cleaned = linksDraft.map(l => l.trim()).filter(Boolean)
		onUpdate({ tripEventLinks: cleaned })
		setIsEditingLinks(false)
	}

	const handleCategoryChange = (newCategory: EventCategory) => {
		if (newCategory !== event.category) {
			onUpdate({ category: newCategory })
		}
	}

	const handleStartTimeChange = (newValue: Dayjs | null) => {
		const formatted = newValue ? newValue.format('HH:mm') : null
		if (formatted !== (event.startTime || null)) {
			onUpdate({ startTime: formatted })
		}
	}

	const description = event.description?.trim()
	const hasLinks = !!event.tripEventLinks?.length
	const hasLocation = !!event.location?.primaryText

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Event Details"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			onDelete={onDelete}
		>
			<Container>
				<TitleInput
					value={title}
					onChange={e => setTitle(e.target.value)}
					onBlur={handleTitleBlur}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault()
							e.currentTarget.blur()
						}
					}}
					placeholder="Event title"
					maxLength={75}
				/>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>Category</FieldLabel>
					</FieldHeader>
					<Select
						value={event.category || 'Other'}
						onChange={e => handleCategoryChange(e.target.value as EventCategory)}
						aria-label="Category"
					>
						{EVENT_CATEGORIES.map(cat => (
							<option key={cat} value={cat}>
								{EVENT_CATEGORY_LABELS[cat]}
							</option>
						))}
					</Select>
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>Start Time</FieldLabel>
					</FieldHeader>
					<TimePickerWrapper>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								value={event.startTime ? dayjs(`2000-01-01T${event.startTime}`) : null}
								onChange={handleStartTimeChange}
								format="HH:mm"
								slotProps={{
									textField: {
										placeholder: 'Select time',
										fullWidth: true,
									},
								}}
							/>
						</LocalizationProvider>
					</TimePickerWrapper>
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>
							<DescriptionGlyph />
							Description
						</FieldLabel>
						{!isEditingDescription && (
							<EditButton onClick={() => setIsEditingDescription(true)}>Edit</EditButton>
						)}
					</FieldHeader>
					{isEditingDescription ? (
						<>
							<Textarea
								value={descriptionDraft}
								onChange={e => setDescriptionDraft(e.target.value)}
								placeholder="Add a description..."
								autoFocus
							/>
							<ButtonRow>
								<SecondaryButton onClick={() => setIsEditingDescription(false)}>
									Cancel
								</SecondaryButton>
								<PrimaryButton onClick={handleSaveDescription}>Save</PrimaryButton>
							</ButtonRow>
						</>
					) : description ? (
						<FieldValue>{description}</FieldValue>
					) : (
						<EmptyValue>No description</EmptyValue>
					)}
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>
							<LocationIcon />
							Location
						</FieldLabel>
						{!isEditingLocation && (
							<EditButton onClick={() => setIsEditingLocation(true)}>Edit</EditButton>
						)}
					</FieldHeader>
					{isEditingLocation ? (
						<>
							<LocationAutocomplete
								value={locationText}
								onChange={text => {
									setLocationText(text)
									setSelectedPlace(null)
								}}
								onPlaceSelect={place => {
									setSelectedPlace(place)
									setLocationText(place.primaryText)
								}}
								placeholder="Search for a location..."
							/>
							{selectedPlace?.secondaryText && (
								<LocationSecondary>{selectedPlace.secondaryText}</LocationSecondary>
							)}
							<ButtonRow>
								{hasLocation && (
									<SecondaryButton onClick={handleClearLocation}>Clear</SecondaryButton>
								)}
								<SecondaryButton onClick={() => setIsEditingLocation(false)}>
									Cancel
								</SecondaryButton>
								<PrimaryButton onClick={handleSaveLocation}>Save</PrimaryButton>
							</ButtonRow>
						</>
					) : hasLocation ? (
						<>
							<LocationDisplay
								href={
									event.location?.googlePlaceId
										? `https://www.google.com/maps/place/?q=place_id:${event.location.googlePlaceId}`
										: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location?.primaryText || '')}`
								}
								target="_blank"
								rel="noopener noreferrer"
							>
								{event.location?.primaryText}
							</LocationDisplay>
							{event.location?.secondaryText && (
								<LocationSecondary>{event.location.secondaryText}</LocationSecondary>
							)}
						</>
					) : (
						<EmptyValue>No location</EmptyValue>
					)}
				</FieldRow>

				<FieldRow>
					<FieldHeader>
						<FieldLabel>
							<LinkIcon />
							Links
						</FieldLabel>
						{!isEditingLinks && (
							<EditButton onClick={() => setIsEditingLinks(true)}>Edit</EditButton>
						)}
					</FieldHeader>
					{isEditingLinks ? (
						<>
							{linksDraft.map((link, index) => (
								<LinkInputWrapper key={index}>
									<LinkTextInput
										type="text"
										placeholder="https://example.com"
										value={link}
										onChange={e => {
											const next = [...linksDraft]
											next[index] = e.target.value
											setLinksDraft(next)
										}}
										autoComplete="off"
									/>
									<RemoveLinkBtn
										type="button"
										onClick={() => setLinksDraft(linksDraft.filter((_, i) => i !== index))}
									>
										Remove
									</RemoveLinkBtn>
								</LinkInputWrapper>
							))}
							<AddLinkBtn type="button" onClick={() => setLinksDraft([...linksDraft, ''])}>
								+ Add Link
							</AddLinkBtn>
							<ButtonRow>
								<SecondaryButton onClick={() => setIsEditingLinks(false)}>Cancel</SecondaryButton>
								<PrimaryButton onClick={handleSaveLinks}>Save</PrimaryButton>
							</ButtonRow>
						</>
					) : hasLinks ? (
						<LinkListReadOnly>
							{event.tripEventLinks?.map(link => {
								const hasProtocol =
									link.url.startsWith('http://') || link.url.startsWith('https://')
								const looksLikeUrl = link.url.includes('.') && !link.url.includes(' ')
								const hrefUrl = !hasProtocol && looksLikeUrl ? `https://${link.url}` : link.url
								const isClickable = hasProtocol || looksLikeUrl
								const content = (
									<>
										<LinkThumbnail $src={link.thumbnail || undefined}>
											{!link.thumbnail && (
												<LinkIcon sx={{ fontSize: 28 }} htmlColor="#9ca3af" />
											)}
										</LinkThumbnail>
										<LinkInfo>
											<LinkSource $color={getSourceColor(link.source)}>
												{formatSourceName(link.source)}
											</LinkSource>
											<LinkUrl>{link.url}</LinkUrl>
										</LinkInfo>
									</>
								)
								return isClickable ? (
									<LinkPreview
										key={link.id}
										href={hrefUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										{content}
									</LinkPreview>
								) : (
									<StaticLinkPreview key={link.id} as="div">
										{content}
									</StaticLinkPreview>
								)
							})}
						</LinkListReadOnly>
					) : (
						<EmptyValue>No links</EmptyValue>
					)}
				</FieldRow>

				{tripId && (
					<AttachmentSection
						tripId={tripId}
						resourceType="TRIP_EVENT"
						resourceId={event.id}
						fileIds={event.fileIds || []}
					/>
				)}
			</Container>
		</Modal>
	)
}

export default EventDetailsModal
